import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper';
import { usePagination } from '../../Home/Components/usePagination';
import { useClassName } from 'common/hooks';
import CTAButton from './CTAButton';
import { useImageSelection } from '../hooks';
import SliderControls from './SliderControls';

import './styles/hero-carousel.less';

const HeroCarousel = ({ events, hasFeaturedEvents }) => {
  const classNames = useClassName('HeroCarousel');
  const [swiperRef, setSwiperRef] = useState(null);
  const [hasMultipleSlides, setHasMultipleSlides] = useState(false);

  const pagination = usePagination({});
  const { getImageComponent } = useImageSelection();

  const handleSwiperInit = (swiper) => {
    setSwiperRef(swiper);

    const bullets = swiper.pagination.bullets;
    setHasMultipleSlides(bullets.length > 1);
  };

  const slidePrev = () => swiperRef?.slidePrev();
  const slideNext = () => swiperRef?.slideNext();

  const handleSlideClick = (slug, e) => {
    if (
      e.target.closest('a') ||
      e.target.closest(`.${classNames('slider-controls-prev')}`) ||
      e.target.closest(`.${classNames('slider-controls-next')}`)
    ) {
      return;
    }
    if (slug) {
      window.location.href = slug;
    }
  };

  const renderSlideContent = (slide) => {
    if (slide.itemType === 'asset') {
      return getImageComponent({
        asset: slide.content,
        className: classNames('image'),
        isHomepageAsset: true,
        poster: null,
        style: { opacity: 1 },
      });
    }

    return getImageComponent({
      asset: slide.asset,
      mobileImage: slide.mobileImage,
      defaultImage: slide.images,
      className: classNames('image'),
    });
  };

  const renderSlideOverlay = (slide) => {
    if (slide.type === 'asset') {
      return null;
    }

    return (
      (slide.showHeroTitle || slide.showHeroCtas) && (
        <>
          <div className={classNames('gradient-overlay')} />
          <div
            className={classNames(
              'info',
              !hasFeaturedEvents ? 'no-featured-info' : ''
            )}>
            {slide.showHeroTitle && (
              <div className={classNames('title')}>{slide.title}</div>
            )}
            {slide.showHeroCtas && (
              <div className={classNames('ctas')}>
                <CTAButton
                  text="Learn More"
                  link={slide.slug}
                  variant="secondary"
                />
                {slide.itemType === 'event' && slide.type && (
                  <CTAButton
                    text={slide.type}
                    link={slide.url}
                    variant="primary"
                  />
                )}
              </div>
            )}
          </div>
        </>
      )
    );
  };

  return (
    <div className={classNames('container')}>
      <Swiper
        modules={[Autoplay, Pagination]}
        {...pagination}
        speed={600}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{ delay: 5000 }}
        onSwiper={handleSwiperInit}
        watchOverflow={true}
        loop={hasMultipleSlides}
        preventClicksPropagation={true}
        className={classNames('wrapper')}>
        {events.map((slide) => (
          <SwiperSlide
            key={slide._id}
            onClick={(e) => handleSlideClick(slide.slug, e)}
            className={classNames('slide')}
            style={{ cursor: slide.slug ? 'pointer' : 'default' }}>
            {renderSlideContent(slide)}
            {renderSlideOverlay(slide)}
            {hasMultipleSlides && (
              <>
                <div
                  className={classNames('slider-controls-prev')}
                  style={{
                    top: hasFeaturedEvents ? '50%' : '40%',
                  }}>
                  <SliderControls direction="prev" onClick={slidePrev} />
                </div>
                <div
                  className={classNames('slider-controls-next')}
                  style={{
                    top: hasFeaturedEvents ? '50%' : '40%',
                  }}>
                  <SliderControls direction="next" onClick={slideNext} />
                </div>
              </>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

HeroCarousel.propTypes = {
  asset: PropTypes.object,
  events: PropTypes.array,
  hasFeaturedEvents: PropTypes.bool,
};

HeroCarousel.defaultProps = {
  events: [],
  hasFeaturedEvents: false,
};

export default HeroCarousel;
